import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ReactFlow,
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  BackgroundVariant,
  MarkerType,
  Panel,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Card } from '@/components/ui/card';
import { Maximize2, X, ChevronDown, ChevronUp, Workflow } from 'lucide-react';
import { FlowChartSpec } from '@/services/agents/flowAgent';

// Add custom styles for controls visibility and performance optimizations
const controlsStyle = document.createElement('style');
controlsStyle.textContent = `
  .flowchart-controls button {
    background-color: white !important;
    color: #262626 !important;
    border: 1px solid #e5e7eb !important;
  }
  .dark .flowchart-controls button,
  .dark-theme .flowchart-controls button {
    background-color: #262626 !important;
    color: white !important;
    border: 1px solid #374151 !important;
  }
  .flowchart-controls button:hover {
    background-color: #f3f4f6 !important;
  }
  .dark .flowchart-controls button:hover,
  .dark-theme .flowchart-controls button:hover {
    background-color: #374151 !important;
  }

  /* Performance optimizations for smooth dragging */
  .react-flow__node {
    will-change: transform;
    transform: translateZ(0);
  }
  .react-flow__nodes {
    will-change: transform;
  }
  .react-flow__viewport {
    will-change: transform;
  }
  .react-flow__edge {
    will-change: transform;
  }
  .react-flow__pane {
    cursor: grab;
  }
  .react-flow__pane:active {
    cursor: grabbing;
  }

  /* React Flow Minimap - responsive styles */
  .react-flow__minimap {
    width: 160px !important;
    height: 120px !important;
    right: 12px !important;
    bottom: 12px !important;
    background: rgba(44, 44, 44, 0.9) !important;
    border: 1px solid #3c3c3c !important;
    border-radius: 8px !important;
  }
  .light-theme .react-flow__minimap {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 1px solid #e5e7eb !important;
  }
  @media (max-width: 1024px) {
    .react-flow__minimap {
      width: 140px !important;
      height: 100px !important;
    }
  }
  @media (max-width: 768px) {
    /* Hide minimap on small screens unless fullscreen is open */
    body:not(.flowchart-fullscreen-open) .react-flow__minimap {
      display: none !important;
    }
    body.flowchart-fullscreen-open .react-flow__minimap {
      width: 120px !important;
      height: 90px !important;
      right: 8px !important;
      bottom: 8px !important;
    }
  }
  @media (max-width: 480px) {
    body.flowchart-fullscreen-open .react-flow__minimap {
      width: 104px !important;
      height: 78px !important;
    }
  }
`;
if (!document.head.querySelector('#flowchart-controls-style')) {
  controlsStyle.id = 'flowchart-controls-style';
  document.head.appendChild(controlsStyle);
}

interface FlowChartRendererProps {
  spec: FlowChartSpec;
  isDarkMode: boolean;
}

// Custom node styles based on type with minimal clean design
const getNodeStyle = (type: string, isDarkMode: boolean) => {
  const baseStyle = {
    padding: '12px 20px',
    borderRadius: type === 'decision' ? '0' : type === 'start' || type === 'end' ? '50px' : '8px',
    fontSize: '14px',
    fontWeight: 500,
    border: '2px solid',
    minWidth: '120px',
    textAlign: 'center' as const,
    boxShadow: isDarkMode
      ? '0 2px 4px rgba(0, 0, 0, 0.2)'
      : '0 2px 4px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.2s ease',
  };

  const styles: Record<string, any> = {
    start: {
      ...baseStyle,
      background: isDarkMode ? '#6366f1' : '#818cf8',
      borderColor: isDarkMode ? '#4f46e5' : '#6366f1',
      color: '#ffffff',
    },
    end: {
      ...baseStyle,
      background: isDarkMode ? '#dc2626' : '#ef4444',
      borderColor: isDarkMode ? '#991b1b' : '#dc2626',
      color: '#ffffff',
    },
    process: {
      ...baseStyle,
      background: isDarkMode ? '#1e293b' : '#ffffff',
      borderColor: isDarkMode ? '#3b82f6' : '#60a5fa',
      color: isDarkMode ? '#ffffff' : '#1e293b',
    },
    decision: {
      ...baseStyle,
      borderRadius: '0',
      transform: 'rotate(45deg)',
      background: isDarkMode ? '#1e293b' : '#ffffff',
      borderColor: isDarkMode ? '#f59e0b' : '#fbbf24',
      borderWidth: '3px',
      color: isDarkMode ? '#ffffff' : '#1e293b',
      minWidth: '140px',
      minHeight: '140px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    input: {
      ...baseStyle,
      background: isDarkMode ? '#1e293b' : '#ffffff',
      borderColor: isDarkMode ? '#10b981' : '#34d399',
      borderWidth: '3px',
      color: isDarkMode ? '#ffffff' : '#1e293b',
      clipPath: 'polygon(10% 0%, 100% 0%, 90% 100%, 0% 100%)',
      minWidth: '150px',
      padding: '16px 24px',
    },
    output: {
      ...baseStyle,
      background: isDarkMode ? '#1e293b' : '#ffffff',
      borderColor: isDarkMode ? '#8b5cf6' : '#a78bfa',
      borderWidth: '3px',
      color: isDarkMode ? '#ffffff' : '#1e293b',
      clipPath: 'polygon(10% 0%, 100% 0%, 90% 100%, 0% 100%)',
      minWidth: '150px',
      padding: '16px 24px',
    },
    // New node types
    database: {
      ...baseStyle,
      background: isDarkMode ? '#1e293b' : '#ffffff',
      borderColor: isDarkMode ? '#0ea5e9' : '#38bdf8',
      borderWidth: '3px',
      color: isDarkMode ? '#ffffff' : '#1e293b',
      borderRadius: '12px',
      clipPath: 'ellipse(50% 40% at 50% 50%)',
      minHeight: '70px',
      padding: '16px 24px',
    },
    document: {
      ...baseStyle,
      background: isDarkMode ? '#1e293b' : '#ffffff',
      borderColor: isDarkMode ? '#eab308' : '#fbbf24',
      borderWidth: '3px',
      color: isDarkMode ? '#ffffff' : '#1e293b',
      clipPath: 'polygon(0 0, 100% 0, 100% 85%, 85% 100%, 0 100%)',
      minHeight: '70px',
      padding: '16px 24px',
    },
    delay: {
      ...baseStyle,
      background: isDarkMode ? '#1e293b' : '#ffffff',
      borderColor: isDarkMode ? '#f97316' : '#fb923c',
      color: isDarkMode ? '#ffffff' : '#1e293b',
      borderRadius: '50px',
      minWidth: '90px',
      minHeight: '90px',
    },
    manual: {
      ...baseStyle,
      background: isDarkMode ? '#1e293b' : '#ffffff',
      borderColor: isDarkMode ? '#ec4899' : '#f472b6',
      borderWidth: '3px',
      color: isDarkMode ? '#ffffff' : '#1e293b',
      clipPath: 'polygon(0 20%, 100% 0, 100% 100%, 0 80%)',
      minHeight: '70px',
      padding: '16px 24px',
    },
  };

  return styles[type] || styles.process;
};

// Custom node component
const CustomNode = ({ data, type, isDarkMode }: any) => {
  const style = getNodeStyle(type, isDarkMode);

  if (type === 'decision') {
    return (
      <div style={style}>
        <div style={{ transform: 'rotate(-45deg)' }}>
          {data.label}
        </div>
      </div>
    );
  }

  return <div style={style}>{data.label}</div>;
};

export const FlowChartRenderer: React.FC<FlowChartRendererProps> = ({ spec, isDarkMode }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLegendExpanded, setIsLegendExpanded] = useState(false);
  const [isFullscreenHeaderExpanded, setIsFullscreenHeaderExpanded] = useState(false);
  const [backgroundVariant, setBackgroundVariant] = useState<BackgroundVariant>(BackgroundVariant.Dots);

  // Prevent body scroll when fullscreen is open
  React.useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('flowchart-fullscreen-open');
    } else {
      document.body.style.overflow = '';
      document.body.classList.remove('flowchart-fullscreen-open');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('flowchart-fullscreen-open');
    };
  }, [isFullscreen]);

  // Transform spec nodes to ReactFlow nodes with custom styling
  const initialNodes: Node[] = spec.nodes.map((node) => ({
    id: node.id,
    type: 'default',
    position: node.position,
    data: {
      label: (
        <CustomNode
          data={node.data}
          type={node.type}
          isDarkMode={isDarkMode}
        />
      ),
    },
    style: {
      background: 'transparent',
      border: 'none',
      padding: 0,
    },
  }));

  // Transform spec edges to ReactFlow edges with enhanced styling
  const initialEdges: Edge[] = spec.edges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    label: edge.label,
    animated: edge.animated || false,
    type: edge.animated ? 'smoothstep' : 'default',
    style: {
      stroke: edge.animated
        ? (isDarkMode ? '#3b82f6' : '#60a5fa')
        : (isDarkMode ? '#64748b' : '#94a3b8'),
      strokeWidth: edge.animated ? 2.5 : 2,
    },
    labelStyle: {
      fill: isDarkMode ? '#e2e8f0' : '#475569',
      fontSize: 12,
      fontWeight: 600,
      background: isDarkMode ? '#1e293b' : '#ffffff',
      padding: '4px 8px',
      borderRadius: '4px',
    },
    labelBgStyle: {
      fill: isDarkMode ? '#1e293b' : '#ffffff',
      fillOpacity: 0.95,
    },
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: edge.animated
        ? (isDarkMode ? '#3b82f6' : '#60a5fa')
        : (isDarkMode ? '#64748b' : '#94a3b8'),
      width: 18,
      height: 18,
    },
  }));

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const toggleBackgroundVariant = () => {
    setBackgroundVariant((prev) => {
      if (prev === BackgroundVariant.Dots) return BackgroundVariant.Lines;
      if (prev === BackgroundVariant.Lines) return BackgroundVariant.Cross;
      return BackgroundVariant.Dots;
    });
  };

  const downloadFlowchart = () => {
    // Create a simple text representation
    const textContent = `
${spec.title}
${spec.description}

Nodes:
${spec.nodes.map(n => `- ${n.data.label} (${n.type})`).join('\n')}

Connections:
${spec.edges.map(e => `- ${e.source} → ${e.target}${e.label ? ` (${e.label})` : ''}`).join('\n')}
    `.trim();

    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${spec.title.replace(/\s+/g, '_')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {/* Normal Card View */}
      <Card className="overflow-hidden w-full">
        {/* Header */}
        <div className="px-3 py-2 border-b border-gray-200 dark:border-[#3c3c3c] bg-gray-50 dark:bg-[#2c2c2c]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div>
                <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                  {spec.title}
                </h4>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsFullscreen(true)}
                className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                title="Open in fullscreen"
              >
                <Maximize2 className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
          </div>
        </div>

        {/* Flowchart Preview */}
        <div
          className="relative w-full"
          style={{
            height: '600px',
            backgroundColor: isDarkMode ? 'rgba(37, 37, 37, 0.9)' : '#ffffff'
          }}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            fitView
            proOptions={{ hideAttribution: true }}
            className={isDarkMode ? 'dark-theme' : 'light-theme'}
            minZoom={0.1}
            maxZoom={2}
            defaultViewport={{ x: 0, y: 0, zoom: 1 }}
            nodesDraggable={true}
            nodesConnectable={true}
            panOnDrag={true}
            panOnScroll={false}
            zoomOnScroll={true}
            zoomOnPinch={true}
            zoomOnDoubleClick={true}
            selectNodesOnDrag={false}
          >
            <Background
              variant={backgroundVariant}
              gap={24}
              size={2}
              color={isDarkMode ? '#6b7280' : '#d1d5db'}
            />
            <Controls
              className="flowchart-controls"
              showInteractive={false}
            />
            <MiniMap
              nodeColor={(node) => {
                const type = spec.nodes.find(n => n.id === node.id)?.type || 'process';
                const colors: Record<string, string> = {
                  start: '#6366f1',
                  end: '#dc2626',
                  process: '#3b82f6',
                  decision: '#f59e0b',
                  input: '#10b981',
                  output: '#8b5cf6',
                  database: '#0ea5e9',
                  document: '#eab308',
                  delay: '#f97316',
                  manual: '#ec4899',
                };
                return colors[type] || '#3b82f6';
              }}
              style={{
                backgroundColor: isDarkMode ? '#262626' : '#ffffff',
                border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
              }}
            />
          </ReactFlow>
        </div>
      </Card>

      {/* Fullscreen Modal - Using Portal for true fullscreen */}
      {isFullscreen && createPortal(
        <div className="fixed inset-0 flex flex-col" style={{ zIndex: 999999, backgroundColor: isDarkMode ? '#2c2c2c' : '#ffffff' }}>
          {/* Collapsible Fullscreen Header */}
          <div className="shrink-0  rounded-b-md border-b border-gray-200 dark:border-gray-700" style={{ backgroundColor: isDarkMode ? '#3c3c3c' : '#f9fafb' }}>
            <div className="flex items-center justify-between p-2">
              <button
                onClick={() => setIsFullscreenHeaderExpanded(!isFullscreenHeaderExpanded)}
                className="flex items-center gap-2 flex-1 text-left hover:opacity-80 transition-opacity"
              >
                {isFullscreenHeaderExpanded ? (
                  <ChevronUp className="w-5 h-5" style={{ color: isDarkMode ? '#ffffff' : '#262626' }} />
                ) : (
                  <ChevronDown className="w-5 h-5" style={{ color: isDarkMode ? '#ffffff' : '#262626' }} />
                )}
                <span className="font-semibold text-md" style={{ color: isDarkMode ? '#ffffff' : '#262626' }}>
                  {spec.title}
                </span>
              </button>
              <button
                onClick={() => setIsFullscreen(false)}
                className="p-2 rounded-lg transition-colors"
                style={{
                  backgroundColor: isDarkMode ? '#2c2c2c' : 'rgb(244, 244, 244)',
                  color: isDarkMode ? '#ffffff' : 'rgb(136, 136, 136)'
                }}
                title="Close fullscreen"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {isFullscreenHeaderExpanded && (
              <div className="px-3 pb-2">
                <p className="text-sm" style={{ color: isDarkMode ? '#d1d5db' : '#6b7280' }}>
                  {spec.description}
                </p>
              </div>
            )}
          </div>

          {/* Fullscreen Flowchart */}
          <div
            className="flex-1 relative overflow-hidden"
            style={{
              backgroundColor: isDarkMode ? 'rgba(37, 37, 37, 0.9)' : '#ffffff'
            }}
          >
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              fitView
              proOptions={{ hideAttribution: true }}
              className={isDarkMode ? 'dark-theme' : 'light-theme'}
              minZoom={0.1}
              maxZoom={2}
              defaultViewport={{ x: 0, y: 0, zoom: 1 }}
              nodesDraggable={true}
              nodesConnectable={true}
              panOnDrag={true}
              panOnScroll={false}
              zoomOnScroll={true}
              zoomOnPinch={true}
              zoomOnDoubleClick={true}
              selectNodesOnDrag={false}
            >
              <Background
                variant={backgroundVariant}
                gap={24}
                size={3}
                color={isDarkMode ? '#6b7280' : '#d1d5db'}
              />
              <Controls
                className="flowchart-controls"
                showInteractive={false}
              />
              <MiniMap
                nodeColor={(node) => {
                  const type = spec.nodes.find(n => n.id === node.id)?.type || 'process';
                  const colors: Record<string, string> = {
                    start: '#6366f1',
                    end: '#dc2626',
                    process: '#3b82f6',
                    decision: '#f59e0b',
                    input: '#10b981',
                    output: '#8b5cf6',
                    database: '#0ea5e9',
                    document: '#eab308',
                    delay: '#f97316',
                    manual: '#ec4899',
                  };
                  return colors[type] || '#3b82f6';
                }}
                style={{
                  backgroundColor: isDarkMode ? '#262626' : '#ffffff',
                  border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                  width: 200,
                  height: 150,
                }}
              />
              <Panel position="top-right" className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg p-3 m-4 shadow-lg">
                <div className="text-xs space-y-1">
                  <div className="text-gray-600 dark:text-gray-400">
                    <span className="font-medium">{spec.nodes.length}</span> nodes
                  </div>
                  <div className="text-gray-600 dark:text-gray-400">
                    <span className="font-medium">{spec.edges.length}</span> connections
                  </div>
                </div>
              </Panel>
            </ReactFlow>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default FlowChartRenderer;
